import { useTranslations } from "@fenetech/translations";
import { Accordion, AccordionDetails, Button, Stack, Typography } from "@mui/material";
import React, { useCallback } from 'react';
import { useCADActions, useCADState } from "../Context/CADContext";
import CADItemAPI from "../API/CADItemAPI";
import { LayerDisplayEnum } from "../enums";
import useIsMobile from "helpers/hooks/useIsMobile";
import AccordionSummarySlim from "components/Common/AccordionSummarySlim";

interface ICADUISettingsProps {
    style?: React.CSSProperties
}

const CADUISettings: React.FC<ICADUISettingsProps> = (props: ICADUISettingsProps) => {

    const tm = useTranslations();
    const cadState = useCADState();
    const cadActions = useCADActions();
    const isMobile = useIsMobile();

    const handleSelectionToggle = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {

        let newUISelections = { ...cadState.model!.uiSelections };

        const selectionPropertyName: string = event.currentTarget.getAttribute("data-value")!;

        if (selectionPropertyName === "layerDisplay") {
            switch (newUISelections.layerDisplay) {
                case LayerDisplayEnum.Single:
                    newUISelections.layerDisplay = LayerDisplayEnum.SubLayers;
                    break;
                case LayerDisplayEnum.SubLayers:
                    newUISelections.layerDisplay = LayerDisplayEnum.AllLayers;
                    break;
                case LayerDisplayEnum.AllLayers:
                    newUISelections.layerDisplay = LayerDisplayEnum.Single;
                    break;
            }
        }
        else {
            //Boolean
            newUISelections[selectionPropertyName] = !newUISelections[selectionPropertyName];
        }

        const newModel = await CADItemAPI.PostUISelections(newUISelections);
        cadActions.setModel(newModel, false);

    }, [cadActions, cadState.model]);



    if (cadState.model === null) return null;

    if (isMobile) {
        return <Accordion
            disableGutters
            style={{ width: "100%" }}
            color="secondary"
        >
            <AccordionSummarySlim>
                <Typography width={"100%"}>{tm.Get("UI Settings")}</Typography>
            </AccordionSummarySlim>
            <AccordionDetails>
                <Stack spacing={1}>
                    <UIButtons handleSelectionToggle={handleSelectionToggle} />
                </Stack>
            </AccordionDetails>
        </Accordion>
    }

    return <>
        <Stack direction="row" justifyContent="center" style={props.style} spacing={.5} >
            <UIButtons handleSelectionToggle={handleSelectionToggle} />
        </Stack>
    </>;
}

interface ICADUIButtonsProps {
    handleSelectionToggle?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const UIButtons: React.FC<ICADUIButtonsProps> = ({ handleSelectionToggle }) => {

    const cadState = useCADState();
    const tm = useTranslations();

    const layerDisplayText: string = cadState.model?.uiSelections.layerDisplay === LayerDisplayEnum.AllLayers
        ? tm.Get("All Layers")
        : cadState.model?.uiSelections.layerDisplay === LayerDisplayEnum.SubLayers
            ? tm.Get("Sub Layers")
            : tm.Get("Single Layer");

    return <>
        <Button size="small"
            data-value="showDimensions"
            variant={cadState.model?.uiSelections.showDimensions ? "contained" : "outlined"}
            color="secondary"
            onClick={handleSelectionToggle}
        >
            <Typography>{tm.Get("Show Drawing Dimensions")}</Typography>
        </Button>

        <Button size="small"
            data-value="showEdgeNumbers"
            variant={cadState.model?.uiSelections.showEdgeNumbers ? "contained" : "outlined"}
            color="secondary"
            onClick={handleSelectionToggle}
        >
            <Typography>{tm.Get("Show Edge Numbers")}</Typography>
        </Button>

        {cadState.model?.layer.layers && cadState.model.layer.layers.length > 0 &&
            <Button size="small"
                data-value="layerDisplay"
                variant="contained"
                color="secondary"
                onClick={handleSelectionToggle}
            >
                <Typography minWidth={150}>{layerDisplayText}</Typography>
            </Button>
        }
    </>
}

export default CADUISettings;